import type { ColumnDef } from '@tanstack/react-table';
import LocationAccuracyTable from './LocationAccuracyTable';
import { accuracyColumn as col } from './locationAccuracyColumns';
import GeofenceRollout from './GeofenceRollout';
import MuleBadge from '../../components/MuleBadge';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronDownIcon,
  ArrowPathIcon,
  BuildingOfficeIcon,
  UserIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import ButtonGroup from '../../components/ButtonGroup';
import { Dropdown } from '../../components/ui/Dropdown';
import Toggle from '../../components/Toggle';

// Types
interface LocationAccuracySummary {
  total_locations: number;
  total_entries: number;
  total_exits: number;
  total_drivers: number;
  total_legs: number;
  days: number;
}

interface LocationData {
  location_code: number;
  location_name: string;
  total_legs: number;
  entry_count: number;
  exit_count: number;
  driver_count: number;
  avg_distance_mi: number | null;
  max_distance_mi: number | null;
  first_event: string | null;
  last_event: string | null;
}

interface DriverData {
  unitnumber: string;
  isMule?: boolean;
  driver_name: string | null;
  total_legs: number;
  locations_visited: number;
  entry_count: number;
  exit_count: number;
  avg_distance_mi: number | null;
  first_event: string | null;
  last_event: string | null;
}

interface ProData {
  pronumber: string;
  leg_count: number;
  unitnumber: string;
  isMule?: boolean;
  driver_name: string | null;
  locations_count: number;
  entry_count: number;
  exit_count: number;
  first_event: string | null;
  last_event: string | null;
}

interface EventData {
  id: number;
  unitnumber: string;
  isMule?: boolean;
  driver_name: string | null;
  pronumber: string;
  legnum: number;
  location_code: number;
  location_name: string;
  location_type: 'ORIGIN' | 'DESTINATION';
  event_type: 'ENTRY' | 'EXIT';
  event_timestamp: string;
  event_latitude: number | null;
  event_longitude: number | null;
  distance_from_center_mi: number | null;
  // Driver-entered times from legscurrent
  origarrivaldatetime: string | null;
  origdepartdatetime: string | null;
  destarrivaldatetime: string | null;
  destdepartdatetime: string | null;
}

interface MissingGeofenceData {
  location_code: number;
  location_name: string;
  issue_count: number;
  missing_origin_entry: number;
  missing_origin_exit: number;
  missing_dest_entry: number;
  missing_dest_exit: number;
  unique_drivers: number;
  driver_total: number;
  geofence_total: number;
}

interface MissingProDetail {
  pronumber: string;
  legnum: number;
  unitnumber: string;
  isMule?: boolean;
  location_type: 'ORIGIN' | 'DESTINATION';
  event_type: 'ENTRY' | 'EXIT';
  driver_entered_time: string | null;
  has_geofence: boolean;
}

interface MissingByDriverData {
  unitnumber: string;
  isMule?: boolean;
  driver_name: string | null;
  missing_count: number;
  driver_total: number;
  geofence_total: number;
  percentage_missing: number;
  locations_visited: number;
}

interface MissingDriverDetail {
  pronumber: string;
  legnum: number;
  unitnumber: string;
  isMule?: boolean;
  location_code: number;
  location_name: string | null;
  location_type: 'ORIGIN' | 'DESTINATION';
  event_type: 'ENTRY' | 'EXIT';
  driver_entered_time: string | null;
  has_geofence: boolean;
}

type MissingDriverSortBy = 'missing' | 'percentage';

interface DriverAccuracyData {
  unitnumber: string;
  isMule?: boolean;
  driver_name: string | null;
  total_legs: number;
  oa_checked?: number; oa_outside?: number; oa_accuracy?: number | null;
  od_checked?: number; od_outside?: number; od_accuracy?: number | null;
  da_checked?: number; da_outside?: number; da_accuracy?: number | null;
  dd_checked?: number; dd_outside?: number; dd_accuracy?: number | null;
  overall_checked: number;
  overall_outside: number;
  overall_accuracy: number | null;
}

interface DriverAccuracyDetail {
  pronumber: string;
  legnum: number;
  unitnumber: string;
  isMule?: boolean;
  location_code: number;
  location_name: string | null;
  event_type: string;
  driver_time: string | null;
  geofence_time: string | null;
  diff_minutes: number;
}

type EventTypeKey = 'oa' | 'od' | 'da' | 'dd';

const EVENT_TYPE_LABELS: Record<EventTypeKey, string> = {
  oa: 'Orig Arrival',
  od: 'Orig Depart',
  da: 'Dest Arrival',
  dd: 'Dest Depart',
};

type ViewMode = 'location' | 'driver' | 'pro' | 'missing' | 'missing-driver' | 'accuracy';

const VIEW_MODE_OPTIONS = [
  { value: 'location', label: 'By Location', icon: BuildingOfficeIcon },
  { value: 'driver', label: 'By Driver', icon: UserIcon },
  { value: 'pro', label: 'By PRO', icon: DocumentTextIcon },
  { value: 'missing', label: 'Missing Geofences', icon: ExclamationTriangleIcon },
  { value: 'missing-driver', label: 'Missing by Driver', icon: UserGroupIcon },
  { value: 'accuracy', label: 'Driver Accuracy', icon: ChartBarIcon },
];

const TIME_FRAME_OPTIONS = [
  { value: 7, label: 'Last 7 Days' },
  { value: 15, label: 'Last 15 Days' },
  { value: 30, label: 'Last 30 Days' },
  { value: 60, label: 'Last 60 Days' },
  { value: 90, label: 'Last 90 Days' },
];

const GeofenceLocationAccuracy: React.FC = () => {
  const [summary, setSummary] = useState<LocationAccuracySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('location');
  const [timeFrame, setTimeFrame] = useState<number>(30);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Data states
  const [locations, setLocations] = useState<LocationData[]>([]);
  const [drivers, setDrivers] = useState<DriverData[]>([]);
  const [pros, setPros] = useState<ProData[]>([]);
  const [events, setEvents] = useState<EventData[]>([]);
  // Drill-down fetch failure flags: a failed expand must not show "no events"
  // (or worse, the PREVIOUS row's rows) — 2026-08-25 sweep.
  const [drilldownFailed, setDrilldownFailed] = useState(false);
  const [missingGeofences, setMissingGeofences] = useState<MissingGeofenceData[]>([]);
  const [missingByDriver, setMissingByDriver] = useState<MissingByDriverData[]>([]);
  const [missingDriverSortBy, setMissingDriverSortBy] = useState<MissingDriverSortBy>('missing');

  // Expanded row states
  const [expandedLocation, setExpandedLocation] = useState<number | null>(null);
  const [expandedDriver, setExpandedDriver] = useState<string | null>(null);
  const [expandedPro, setExpandedPro] = useState<string | null>(null);
  const [expandedMissingLocation, setExpandedMissingLocation] = useState<number | null>(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [missingProDetails, setMissingProDetails] = useState<MissingProDetail[]>([]);
  const [missingProLoading, setMissingProLoading] = useState(false);
  const [missingDriverDetails, setMissingDriverDetails] = useState<MissingDriverDetail[]>([]);
  const [missingDriverDetailLoading, setMissingDriverDetailLoading] = useState(false);

  // Driver accuracy states
  const [accuracyData, setAccuracyData] = useState<DriverAccuracyData[]>([]);
  const [accuracyDetails, setAccuracyDetails] = useState<DriverAccuracyDetail[]>([]);
  const [accuracyDetailLoading, setAccuracyDetailLoading] = useState(false);
  const [expandedAccuracyDriver, setExpandedAccuracyDriver] = useState<string | null>(null);
  const [thresholdMinutes, setThresholdMinutes] = useState<number>(15);
  const [accuracySortBy, setAccuracySortBy] = useState<'accuracy' | 'outside'>('accuracy');
  const [eventTypes, setEventTypes] = useState<Record<EventTypeKey, boolean>>({
    oa: true, od: true, da: true, dd: true,
  });

  // Pagination
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const limit = 50;

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get<LocationAccuracySummary>(
        `/geofences/location-accuracy/summary?days=${timeFrame}`
      );
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  }, [timeFrame]);

  // Fetch locations
  const fetchLocations = useCallback(async (resetOffset = false, requestedOffset = offset) => {
    setLoading(true);
    try {
      const newOffset = resetOffset ? 0 : requestedOffset;
      const params = new URLSearchParams({
        days: timeFrame.toString(),
        limit: limit.toString(),
        offset: newOffset.toString(),
      });
      if (search) params.append('search', search);

      const response = await api.get<{ data: LocationData[]; total: number; has_more: boolean }>(
        `/geofences/location-accuracy/by-location?${params.toString()}`
      );
      setLocations(response.data.data);
      setTotal(response.data.total);
      setHasMore(response.data.has_more);
      if (resetOffset) setOffset(0);
    } catch (err) {
      setError('Failed to fetch location data');
    } finally {
      setLoading(false);
    }
  }, [timeFrame, search, offset]);

  // Fetch drivers
  const fetchDrivers = useCallback(async (resetOffset = false, requestedOffset = offset) => {
    setLoading(true);
    try {
      const newOffset = resetOffset ? 0 : requestedOffset;
      const params = new URLSearchParams({
        days: timeFrame.toString(),
        limit: limit.toString(),
        offset: newOffset.toString(),
      });
      if (search) params.append('search', search);

      const response = await api.get<{ data: DriverData[]; total: number; has_more: boolean }>(
        `/geofences/location-accuracy/by-driver?${params.toString()}`
      );
      setDrivers(response.data.data);
      setTotal(response.data.total);
      setHasMore(response.data.has_more);
      if (resetOffset) setOffset(0);
    } catch (err) {
      setError('Failed to fetch driver data');
    } finally {
      setLoading(false);
    }
  }, [timeFrame, search, offset]);

  // Fetch PROs
  const fetchPros = useCallback(async (resetOffset = false, requestedOffset = offset) => {
    setLoading(true);
    try {
      const newOffset = resetOffset ? 0 : requestedOffset;
      const params = new URLSearchParams({
        days: timeFrame.toString(),
        limit: limit.toString(),
        offset: newOffset.toString(),
      });
      if (search) params.append('search', search);

      const response = await api.get<{ data: ProData[]; total: number; has_more: boolean }>(
        `/geofences/location-accuracy/by-pro?${params.toString()}`
      );
      setPros(response.data.data);
      setTotal(response.data.total);
      setHasMore(response.data.has_more);
      if (resetOffset) setOffset(0);
    } catch (err) {
      setError('Failed to fetch PRO data');
    } finally {
      setLoading(false);
    }
  }, [timeFrame, search, offset]);

  // Fetch missing geofences
  const fetchMissing = useCallback(async (resetOffset = false, requestedOffset = offset) => {
    setLoading(true);
    try {
      const newOffset = resetOffset ? 0 : requestedOffset;
      const params = new URLSearchParams({
        days: timeFrame.toString(),
        limit: limit.toString(),
        offset: newOffset.toString(),
      });
      if (search) params.append('search', search);

      const response = await api.get<{ data: MissingGeofenceData[]; total: number; has_more: boolean }>(
        `/geofences/location-accuracy/missing-geofences?${params.toString()}`
      );
      setMissingGeofences(response.data.data);
      setTotal(response.data.total);
      setHasMore(response.data.has_more);
      if (resetOffset) setOffset(0);
    } catch (err) {
      setError('Failed to fetch missing geofences data');
    } finally {
      setLoading(false);
    }
  }, [timeFrame, search, offset]);

  // Fetch missing geofences by driver
  const fetchMissingByDriver = useCallback(async (resetOffset = false, requestedOffset = offset) => {
    setLoading(true);
    try {
      const newOffset = resetOffset ? 0 : requestedOffset;
      const params = new URLSearchParams({
        days: timeFrame.toString(),
        limit: limit.toString(),
        offset: newOffset.toString(),
        sort_by: missingDriverSortBy,
      });
      if (search) params.append('search', search);

      const response = await api.get<{ data: MissingByDriverData[]; total: number; has_more: boolean }>(
        `/geofences/location-accuracy/missing-geofences/by-driver?${params.toString()}`
      );
      setMissingByDriver(response.data.data);
      setTotal(response.data.total);
      setHasMore(response.data.has_more);
      if (resetOffset) setOffset(0);
    } catch (err) {
      setError('Failed to fetch missing by driver data');
    } finally {
      setLoading(false);
    }
  }, [timeFrame, search, offset, missingDriverSortBy]);

  // Fetch events for expanded row
  const fetchEvents = useCallback(async (
    locationCode?: number,
    unitnumber?: string,
    pronumber?: string
  ) => {
    setEventsLoading(true);
    setEvents([]); // never show the previous row's rows on a failed expand
    setDrilldownFailed(false);
    try {
      const params = new URLSearchParams({ days: timeFrame.toString(), limit: '50' });
      if (locationCode) params.append('location_code', locationCode.toString());
      if (unitnumber) params.append('unitnumber', unitnumber);
      if (pronumber) params.append('pronumber', pronumber);

      const response = await api.get<{ data: EventData[] }>(
        `/geofences/location-accuracy/events?${params.toString()}`
      );
      setEvents(response.data.data);
    } catch (err) {
      console.error('Error fetching events:', err);
      setDrilldownFailed(true);
    } finally {
      setEventsLoading(false);
    }
  }, [timeFrame]);

  // Fetch missing PRO details for expanded location
  const fetchMissingProDetails = useCallback(async (locationCode: number) => {
    setMissingProLoading(true);
    setMissingProDetails([]); // never show the previous row's rows on a failed expand
    setDrilldownFailed(false);
    try {
      const response = await api.get<{ data: MissingProDetail[] }>(
        `/geofences/location-accuracy/missing-geofences/by-location/${locationCode}?days=${timeFrame}`
      );
      setMissingProDetails(response.data.data);
    } catch (err) {
      console.error('Error fetching missing PRO details:', err);
      setDrilldownFailed(true);
    } finally {
      setMissingProLoading(false);
    }
  }, [timeFrame]);

  // Handle expand missing location
  const handleExpandMissingLocation = (locationCode: number) => {
    if (expandedMissingLocation === locationCode) {
      setExpandedMissingLocation(null);
      setMissingProDetails([]);
    } else {
      setExpandedMissingLocation(locationCode);
      fetchMissingProDetails(locationCode);
    }
  };

  // Enabled event types for accuracy view
  const enabledEventTypes = useMemo(() =>
    (Object.keys(eventTypes) as EventTypeKey[]).filter(k => eventTypes[k]).join(','),
    [eventTypes]
  );

  // Effect to fetch data based on view mode
  useEffect(() => {
    fetchSummary();
    if (viewMode === 'location') fetchLocations(true);
    else if (viewMode === 'driver') fetchDrivers(true);
    else if (viewMode === 'pro') fetchPros(true);
    else if (viewMode === 'missing') fetchMissing(true);
    else if (viewMode === 'missing-driver') fetchMissingByDriver(true);
    else if (viewMode === 'accuracy') fetchDriverAccuracy(true);
  }, [viewMode, timeFrame, search, missingDriverSortBy, thresholdMinutes, enabledEventTypes, accuracySortBy]);

  // A drill-down belongs to the selected query/page, not the next result set.
  useEffect(() => {
    setExpandedLocation(null); setExpandedDriver(null); setExpandedPro(null);
    setExpandedMissingLocation(null); setExpandedMissingDriver(null); setExpandedAccuracyDriver(null);
  }, [viewMode, timeFrame, search, offset, thresholdMinutes, enabledEventTypes]);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  // Handle expand/collapse
  const handleExpandLocation = (code: number) => {
    if (expandedLocation === code) {
      setExpandedLocation(null);
      setEvents([]);
    } else {
      setExpandedLocation(code);
      fetchEvents(code);
    }
  };

  const handleExpandDriver = (unitnumber: string) => {
    if (expandedDriver === unitnumber) {
      setExpandedDriver(null);
      setEvents([]);
    } else {
      setExpandedDriver(unitnumber);
      fetchEvents(undefined, unitnumber);
    }
  };

  const handleExpandPro = (pronumber: string) => {
    if (expandedPro === pronumber) {
      setExpandedPro(null);
      setEvents([]);
    } else {
      setExpandedPro(pronumber);
      fetchEvents(undefined, undefined, pronumber);
    }
  };

  // Fetch missing event details for a specific driver
  const fetchMissingDriverDetails = useCallback(async (unitnumber: string) => {
    setMissingDriverDetailLoading(true);
    setMissingDriverDetails([]); // never show the previous row's rows on a failed expand
    setDrilldownFailed(false);
    try {
      const response = await api.get<{ data: MissingDriverDetail[] }>(
        `/geofences/location-accuracy/missing-geofences/by-driver/${encodeURIComponent(unitnumber)}?days=${timeFrame}`
      );
      setMissingDriverDetails(response.data.data);
    } catch (err) {
      console.error('Error fetching missing driver details:', err);
      setDrilldownFailed(true);
    } finally {
      setMissingDriverDetailLoading(false);
    }
  }, [timeFrame]);

  // Expand missing-by-driver row
  const [expandedMissingDriver, setExpandedMissingDriver] = useState<string | null>(null);
  const handleExpandMissingDriver = (unitnumber: string) => {
    if (expandedMissingDriver === unitnumber) {
      setExpandedMissingDriver(null);
      setMissingDriverDetails([]);
    } else {
      setExpandedMissingDriver(unitnumber);
      fetchMissingDriverDetails(unitnumber);
    }
  };

  // Fetch driver accuracy summary
  const fetchDriverAccuracy = useCallback(async (resetOffset = false, requestedOffset = offset) => {
    setLoading(true);
    try {
      const newOffset = resetOffset ? 0 : requestedOffset;
      const params = new URLSearchParams({
        days: timeFrame.toString(),
        limit: limit.toString(),
        offset: newOffset.toString(),
        threshold_minutes: thresholdMinutes.toString(),
        event_types: enabledEventTypes,
        sort_by: accuracySortBy,
      });
      if (search) params.append('search', search);

      const response = await api.get<{ data: DriverAccuracyData[]; total: number; has_more: boolean }>(
        `/geofences/location-accuracy/driver-accuracy?${params.toString()}`
      );
      setAccuracyData(response.data.data);
      setTotal(response.data.total);
      setHasMore(response.data.has_more);
      if (resetOffset) setOffset(0);
    } catch (err) {
      setError('Failed to fetch driver accuracy data');
    } finally {
      setLoading(false);
    }
  }, [timeFrame, search, offset, thresholdMinutes, enabledEventTypes, accuracySortBy]);

  // Fetch accuracy details for a specific driver
  const fetchAccuracyDetails = useCallback(async (unitnumber: string) => {
    setAccuracyDetailLoading(true);
    setAccuracyDetails([]);
    setDrilldownFailed(false);
    try {
      const params = new URLSearchParams({
        days: timeFrame.toString(),
        threshold_minutes: thresholdMinutes.toString(),
        event_types: enabledEventTypes,
      });
      const response = await api.get<{ data: DriverAccuracyDetail[] }>(
        `/geofences/location-accuracy/driver-accuracy/${encodeURIComponent(unitnumber)}?${params.toString()}`
      );
      setAccuracyDetails(response.data.data);
    } catch (err) {
      console.error('Error fetching accuracy details:', err);
      setDrilldownFailed(true);
    } finally {
      setAccuracyDetailLoading(false);
    }
  }, [timeFrame, thresholdMinutes, enabledEventTypes]);

  // Expand accuracy driver row
  const handleExpandAccuracyDriver = (unitnumber: string) => {
    if (expandedAccuracyDriver === unitnumber) {
      setExpandedAccuracyDriver(null);
      setAccuracyDetails([]);
    } else {
      setExpandedAccuracyDriver(unitnumber);
      fetchAccuracyDetails(unitnumber);
    }
  };

  // Pagination handlers
  const handleNextPage = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    if (viewMode === 'location') fetchLocations(false, newOffset);
    else if (viewMode === 'driver') fetchDrivers(false, newOffset);
    else if (viewMode === 'pro') fetchPros(false, newOffset);
    else if (viewMode === 'missing') fetchMissing(false, newOffset);
    else if (viewMode === 'missing-driver') fetchMissingByDriver(false, newOffset);
    else if (viewMode === 'accuracy') fetchDriverAccuracy(false, newOffset);
  };

  const handlePrevPage = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
    if (viewMode === 'location') fetchLocations(false, newOffset);
    else if (viewMode === 'driver') fetchDrivers(false, newOffset);
    else if (viewMode === 'pro') fetchPros(false, newOffset);
    else if (viewMode === 'missing') fetchMissing(false, newOffset);
    else if (viewMode === 'missing-driver') fetchMissingByDriver(false, newOffset);
    else if (viewMode === 'accuracy') fetchDriverAccuracy(false, newOffset);
  };

  // Format date helper
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  // Format UTC timestamp to Central time (for geofence event times from API)
  const formatUtcDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const utcDate = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    return new Date(utcDate).toLocaleString('en-US', {
      timeZone: 'America/Chicago',
    });
  };

  // Get driver-entered time based on event type
  const getDriverEnteredTime = (event: EventData): string | null => {
    if (event.location_type === 'ORIGIN') {
      return event.event_type === 'ENTRY' ? event.origarrivaldatetime : event.origdepartdatetime;
    } else {
      return event.event_type === 'ENTRY' ? event.destarrivaldatetime : event.destdepartdatetime;
    }
  };

  const proLink = (row: { pronumber: string; legnum: number }) => (
    <a href={`/geofencing/driver-travel-history?pro=${encodeURIComponent(row.pronumber)}&legnum=${row.legnum}`}
      target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
      {row.pronumber}-{row.legnum}
    </a>
  );
  const unitCell = (row: { unitnumber: string; isMule?: boolean }) => <>{row.unitnumber}<MuleBadge isMule={row.isMule} /></>;
  const detailsAction = <T,>(select: (row: T) => void): ColumnDef<T> => ({
    id: 'actions', header: 'Details', enableSorting: false, enableColumnFilter: false,
    cell: ({ row }) => <button className="text-blue-600 hover:underline" onClick={() => select(row.original)}>View details</button>,
  });
  const locationColumns: ColumnDef<LocationData>[] = [
    detailsAction(row => handleExpandLocation(row.location_code)), col<LocationData>('location_name', 'Location'), col<LocationData>('location_code', 'Code'),
    col<LocationData>('total_legs', 'Legs'), col<LocationData>('entry_count', 'Entries'), col<LocationData>('exit_count', 'Exits'), col<LocationData>('driver_count', 'Drivers'),
    col<LocationData>('avg_distance_mi', 'Avg distance (mi)', row => row.avg_distance_mi?.toFixed(2) ?? '—'),
  ];
  const driverColumns: ColumnDef<DriverData>[] = [
    detailsAction(row => handleExpandDriver(row.unitnumber)), col<DriverData>('unitnumber', 'Unit', unitCell), col<DriverData>('driver_name', 'Driver'),
    col<DriverData>('total_legs', 'Legs'), col<DriverData>('locations_visited', 'Locations'), col<DriverData>('entry_count', 'Entries'), col<DriverData>('exit_count', 'Exits'),
    col<DriverData>('avg_distance_mi', 'Avg distance (mi)', row => row.avg_distance_mi?.toFixed(2) ?? '—'),
  ];
  const proColumns: ColumnDef<ProData>[] = [
    detailsAction(row => handleExpandPro(row.pronumber)), col<ProData>('pronumber', 'PRO'), col<ProData>('leg_count', 'Legs'),
    col<ProData>('unitnumber', 'Unit', unitCell), col<ProData>('driver_name', 'Driver'), col<ProData>('locations_count', 'Locations'),
    col<ProData>('entry_count', 'Entries'), col<ProData>('exit_count', 'Exits'),
  ];
  const missingColumns: ColumnDef<MissingGeofenceData>[] = [
    detailsAction(row => handleExpandMissingLocation(row.location_code)), col<MissingGeofenceData>('location_name', 'Location'), col<MissingGeofenceData>('location_code', 'Code'),
    col<MissingGeofenceData>('issue_count', 'Missing'), { id: 'percentage_missing', header: '% missing',
      accessorFn: row => row.driver_total ? Math.round(row.issue_count / row.driver_total * 1000) / 10 : 0 },
    col<MissingGeofenceData>('driver_total', 'Driver stamps'), col<MissingGeofenceData>('geofence_total', 'Geofence stamps'),
    col<MissingGeofenceData>('missing_origin_entry', 'Missing origin arrival'), col<MissingGeofenceData>('missing_origin_exit', 'Missing origin departure'),
    col<MissingGeofenceData>('missing_dest_entry', 'Missing destination arrival'), col<MissingGeofenceData>('missing_dest_exit', 'Missing destination departure'),
  ];
  const missingDriverColumns: ColumnDef<MissingByDriverData>[] = [
    detailsAction(row => handleExpandMissingDriver(row.unitnumber)), col<MissingByDriverData>('unitnumber', 'Unit', unitCell), col<MissingByDriverData>('driver_name', 'Driver'),
    col<MissingByDriverData>('missing_count', 'Missing'), col<MissingByDriverData>('percentage_missing', '% missing'), col<MissingByDriverData>('driver_total', 'Driver stamps'),
    col<MissingByDriverData>('geofence_total', 'Geofence stamps'), col<MissingByDriverData>('locations_visited', 'Locations'),
  ];
  const accuracyColumns: ColumnDef<DriverAccuracyData>[] = [
    detailsAction(row => handleExpandAccuracyDriver(row.unitnumber)), col<DriverAccuracyData>('unitnumber', 'Unit', unitCell), col<DriverAccuracyData>('driver_name', 'Driver'),
    col<DriverAccuracyData>('overall_accuracy', 'Overall accuracy (%)'), col<DriverAccuracyData>('overall_outside', 'Outside threshold'), col<DriverAccuracyData>('overall_checked', 'Checked'),
    ...(Object.keys(EVENT_TYPE_LABELS) as EventTypeKey[]).filter(key => eventTypes[key]).flatMap(key => [
      col<DriverAccuracyData>(`${key}_accuracy`, `${EVENT_TYPE_LABELS[key]} accuracy (%)`),
      col<DriverAccuracyData>(`${key}_outside`, `${EVENT_TYPE_LABELS[key]} outside`),
      col<DriverAccuracyData>(`${key}_checked`, `${EVENT_TYPE_LABELS[key]} checked`),
    ]),
  ];
  const eventColumns: ColumnDef<EventData>[] = [
    col<EventData>('event_timestamp', 'Geofence time', row => formatUtcDate(row.event_timestamp)),
    { id: 'driver_time', header: 'Driver entered', accessorFn: getDriverEnteredTime, cell: ({ row }) => formatDate(getDriverEnteredTime(row.original)) },
    col<EventData>('location_type', 'Location type'), col<EventData>('event_type', 'Event'), col<EventData>('location_name', 'Location'),
    col<EventData>('pronumber', 'PRO', proLink), col<EventData>('legnum', 'Leg'), col<EventData>('unitnumber', 'Unit', unitCell),
    col<EventData>('distance_from_center_mi', 'Distance (mi)', row => row.distance_from_center_mi?.toFixed(2) ?? '—'),
  ];
  const missingProColumns: ColumnDef<MissingProDetail>[] = [
    col<MissingProDetail>('pronumber', 'PRO', proLink), col<MissingProDetail>('legnum', 'Leg'), col<MissingProDetail>('unitnumber', 'Unit', unitCell),
    col<MissingProDetail>('location_type', 'Location type'), col<MissingProDetail>('event_type', 'Event'),
    col<MissingProDetail>('driver_entered_time', 'Driver entered', row => formatDate(row.driver_entered_time)), col<MissingProDetail>('has_geofence', 'Has geofence'),
  ];
  const missingDetailColumns: ColumnDef<MissingDriverDetail>[] = [
    col<MissingDriverDetail>('pronumber', 'PRO', proLink), col<MissingDriverDetail>('legnum', 'Leg'), col<MissingDriverDetail>('location_name', 'Location'), col<MissingDriverDetail>('location_code', 'Code'),
    col<MissingDriverDetail>('location_type', 'Location type'), col<MissingDriverDetail>('event_type', 'Event'),
    col<MissingDriverDetail>('driver_entered_time', 'Driver entered', row => formatDate(row.driver_entered_time)), col<MissingDriverDetail>('has_geofence', 'Has geofence'),
  ];
  const accuracyDetailColumns: ColumnDef<DriverAccuracyDetail>[] = [
    col<DriverAccuracyDetail>('pronumber', 'PRO', proLink), col<DriverAccuracyDetail>('legnum', 'Leg'), col<DriverAccuracyDetail>('location_name', 'Location'), col<DriverAccuracyDetail>('location_code', 'Code'),
    col<DriverAccuracyDetail>('event_type', 'Event'), col<DriverAccuracyDetail>('driver_time', 'Driver time', row => formatDate(row.driver_time)),
    col<DriverAccuracyDetail>('geofence_time', 'Geofence time', row => formatUtcDate(row.geofence_time)), col<DriverAccuracyDetail>('diff_minutes', 'Difference (min)'),
  ];
  const renderEventsTable = (title: string) => <LocationAccuracyTable name="events" title={title} data={events} columns={eventColumns}
    loading={eventsLoading} error={drilldownFailed ? 'Could not load events. Close and reopen details to retry.' : null} serverPage />;

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-24">
      {/* Filter Bar */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 px-6 py-2 bg-theme-bg-tertiary border-b border-theme-border-primary">
        <span className="text-sm font-medium text-label">Filters:</span>

        {/* Time Frame */}
        <Dropdown
          trigger={
            <button className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-panel text-body hover:bg-highlight">
              {TIME_FRAME_OPTIONS.find(o => o.value === timeFrame)?.label || 'Last 30 Days'}
              <ChevronDownIcon className="h-3 w-3" />
            </button>
          }
          items={TIME_FRAME_OPTIONS.map((option) => ({
            label: option.label,
            onClick: () => setTimeFrame(option.value),
          }))}
          placement="bottom-start"
        />

        {/* View Mode */}
        <ButtonGroup
          options={VIEW_MODE_OPTIONS}
          value={viewMode}
          onChange={(value) => setViewMode(value as ViewMode)}
          size="sm"
        />

        {/* Spacer */}
        <div className="flex-1" />

        {/* Search */}
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search..."
              className="w-48 pl-8 pr-8 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-body focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => { setSearchInput(''); setSearch(''); }}
                className="absolute right-2 top-1/2 transform -translate-y-1/2"
              >
                <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(''); setSearchInput(''); }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Accuracy Controls Bar */}
      {viewMode === 'accuracy' && (
        <div className="flex flex-wrap items-center gap-4 px-6 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Threshold:</label>
            <input
              type="number"
              min={1}
              max={120}
              value={thresholdMinutes}
              onChange={(e) => setThresholdMinutes(Math.max(1, Math.min(120, parseInt(e.target.value) || 15)))}
              className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-body focus:ring-1 focus:ring-indigo-500"
            />
            <span className="text-xs text-muted">min</span>
          </div>
          <div className="h-4 border-l border-gray-300 dark:border-gray-600" />
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Events:</span>
            {(Object.keys(EVENT_TYPE_LABELS) as EventTypeKey[]).map((key) => (
              <Toggle
                key={key}
                checked={eventTypes[key]}
                onChange={(checked) => setEventTypes(prev => ({ ...prev, [key]: checked }))}
                size="sm"
                label={EVENT_TYPE_LABELS[key]}
                variant="with-label"
              />
            ))}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 px-6 pt-4">
          <div className="bg-panel border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-muted">Locations</div>
            <div className="text-2xl font-bold text-heading">
              {summary.total_locations.toLocaleString()}
            </div>
          </div>
          <div className="bg-panel border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-muted">Drivers</div>
            <div className="text-2xl font-bold text-heading">
              {summary.total_drivers.toLocaleString()}
            </div>
          </div>
          <div className="bg-panel border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-muted">Legs Tracked</div>
            <div className="text-2xl font-bold text-heading">
              {summary.total_legs.toLocaleString()}
            </div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="text-sm text-green-700 dark:text-green-400">Entries</div>
            <div className="text-2xl font-bold text-green-700 dark:text-green-400">
              {summary.total_entries.toLocaleString()}
            </div>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="text-sm text-red-700 dark:text-red-400">Exits</div>
            <div className="text-2xl font-bold text-red-700 dark:text-red-400">
              {summary.total_exits.toLocaleString()}
            </div>
          </div>
        </div>
      )}

      {viewMode === 'missing-driver' && <label className="block px-6 py-2 text-sm">Result order <select aria-label="Missing driver result order" value={missingDriverSortBy} onChange={event => setMissingDriverSortBy(event.target.value as MissingDriverSortBy)} className="border rounded bg-panel p-1"><option value="missing">Most missing</option><option value="percentage">Highest missing percentage</option></select></label>}
      {viewMode === 'accuracy' && <label className="block px-6 py-2 text-sm">Result order <select aria-label="Accuracy result order" value={accuracySortBy} onChange={event => setAccuracySortBy(event.target.value as 'accuracy' | 'outside')} className="border rounded bg-panel p-1"><option value="accuracy">Lowest accuracy</option><option value="outside">Most outside threshold</option></select></label>}
      {/* Data Table */}
      <div className="px-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-500" />
          </div>
        ) : (
          <div className="bg-panel border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            {viewMode === 'location' && <>
              <LocationAccuracyTable name="by-location" title="By location" data={locations} columns={locationColumns} serverPage onRowClick={row => handleExpandLocation(row.location_code)} />
              {expandedLocation !== null && renderEventsTable(`Events · location ${expandedLocation}`)}
            </>}
            {viewMode === 'driver' && <>
              <LocationAccuracyTable name="by-driver" title="By driver" data={drivers} columns={driverColumns} serverPage onRowClick={row => handleExpandDriver(row.unitnumber)} />
              {expandedDriver !== null && renderEventsTable(`Events · unit ${expandedDriver}`)}
            </>}
            {viewMode === 'pro' && <>
              <LocationAccuracyTable name="by-pro" title="By PRO" data={pros} columns={proColumns} serverPage onRowClick={row => handleExpandPro(row.pronumber)} />
              {expandedPro !== null && renderEventsTable(`Events · PRO ${expandedPro}`)}
            </>}
            {viewMode === 'missing' && <>
              <LocationAccuracyTable name="missing-locations" title="Missing geofences by location" data={missingGeofences} columns={missingColumns} serverPage onRowClick={row => handleExpandMissingLocation(row.location_code)} />
              {expandedMissingLocation !== null && <LocationAccuracyTable name="missing-location-details" title={`Missing stamps · location ${expandedMissingLocation}`} data={missingProDetails} columns={missingProColumns} loading={missingProLoading} error={drilldownFailed ? 'Could not load details. Close and reopen to retry.' : null} />}
            </>}
            {viewMode === 'missing-driver' && <>
              <LocationAccuracyTable name="missing-drivers" title="Missing geofences by driver" data={missingByDriver} columns={missingDriverColumns} serverPage onRowClick={row => handleExpandMissingDriver(row.unitnumber)} />
              {expandedMissingDriver !== null && <LocationAccuracyTable name="missing-driver-details" title={`Missing stamps · unit ${expandedMissingDriver}`} data={missingDriverDetails} columns={missingDetailColumns} loading={missingDriverDetailLoading} error={drilldownFailed ? 'Could not load details. Close and reopen to retry.' : null} />}
            </>}
            {viewMode === 'accuracy' && <>
              <LocationAccuracyTable name="driver-accuracy" title="Driver accuracy" data={accuracyData} columns={accuracyColumns} serverPage onRowClick={row => handleExpandAccuracyDriver(row.unitnumber)} />
              {expandedAccuracyDriver !== null && <LocationAccuracyTable name="accuracy-details" title={`Outside threshold · unit ${expandedAccuracyDriver}`} data={accuracyDetails} columns={accuracyDetailColumns} loading={accuracyDetailLoading} error={drilldownFailed ? 'Could not load details. Close and reopen to retry.' : null} />}
            </>}

            {/* Pagination */}
            {total > 0 && (
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={offset === 0}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={!hasMore}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function LocationAccuracyPage() {
  const [tab, setTab] = useState<'existing' | 'rollout'>('existing');
  return <>
    <div className="flex gap-3 px-6 py-3 border-b border-theme-border-primary">
      <button aria-pressed={tab === 'existing'} onClick={() => setTab('existing')} className="px-3 py-2 rounded border">Location accuracy</button>
      <button aria-pressed={tab === 'rollout'} onClick={() => setTab('rollout')} className="px-3 py-2 rounded border">Single-dispatch rollout</button>
    </div>
    {tab === 'rollout' ? <GeofenceRollout /> : <GeofenceLocationAccuracy />}
  </>;
}

export default LocationAccuracyPage;
